/**
 * Remote issues (spec §9.22): task type plugins that mirror an issue tracker announce a quick-add
 * prefix (`quickAddTokens: ["fj"]`); `fj:owner/repo#123` then resolves to the existing or a new task.
 * The host knows no provider by name — everything comes from the task type registry.
 */
import { listTaskTypes } from "../registry/taskTypes";

export interface RemoteRef {
  typeId: string;
  repo: string;
  number: number;
}

export interface RemoteConnection {
  type: string;
  plugin: string;
  id: string;
  host: string | null;
  repos: string[];
  twoWay: Record<string, boolean>;
  webhookUrl: string | null;
  /** i18n keys of what keeps the connection from working as set up */
  problems?: string[];
}

/** `fj:owner/repo#123` (the whole input) → a reference, when some registered task type claims the prefix. */
export function remoteRef(input: string): RemoteRef | null {
  const m = /^([a-z][a-z0-9]*):([\w.-]+\/[\w.-]+)#(\d+)$/i.exec(input.trim());
  if (!m) return null;
  const type = listTaskTypes().find((t) => t.quickAddTokens?.includes(m[1].toLowerCase()));
  return type ? { typeId: type.id, repo: m[2], number: Number(m[3]) } : null;
}

async function call<T>(workspaceId: string, method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api/v1${path}?workspace=${encodeURIComponent(workspaceId)}`, {
    method,
    credentials: "same-origin",
    headers: body === undefined ? {} : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = (await res.json()) as T & { error?: string };
  if (!res.ok) throw new Error(data.error ?? "error.generic");
  return data;
}

/** The task id for a remote issue; the server creates the task (as its own peer) when it is new. */
export async function resolveRemote(workspaceId: string, ref: RemoteRef): Promise<string> {
  const res = await call<{ task: { id: string } }>(workspaceId, "POST", "/remote/resolve", {
    type: ref.typeId,
    repo: ref.repo,
    number: ref.number,
  });
  return res.task.id;
}

export async function listConnections(workspaceId: string): Promise<RemoteConnection[]> {
  return (await call<{ connections: RemoteConnection[] }>(workspaceId, "GET", "/remote/connections"))
    .connections;
}

/** Create the remote issue from a task's title and notes and link the task to it. */
export async function openAsIssue(
  workspaceId: string,
  taskId: string,
  conn: { type: string; id: string },
  repo: string,
): Promise<void> {
  await call(workspaceId, "POST", `/tasks/${taskId}/remote`, {
    type: conn.type,
    connectionId: conn.id,
    repo,
  });
}
